import { DiscussionWithoutContentDTO } from "@/generated-client";
import { ClientFactory } from "@/main/common/ClientFactory";
import { HomeOrderingStyle, HomeRange, homeOrderingSupportsRange } from "./HomePageShared";

/**
 * Resolving the home feed's time window to the smallest one that still has a feed in it.
 *
 * <p>The three count-based sorts (Most upvoted / Most discussed / Most controversial) carry a Range
 * pill whose persisted default is all-time for every member, and `HomePage.handleSelectionChange`
 * restores the REMEMBERED range on every switch between sorts. So a member who changes the sort in
 * order to see something different is served the site's lifetime leaderboard, which on this corpus
 * barely moves — `HomeFeedSettings.AFFINITY_ENGAGEMENT_COEFFICIENT`'s javadoc measured 27 of the top
 * 30 as older than 30 days. Raising the DEFAULT does not reach anyone: every existing member has
 * `ALL` persisted explicitly and there is no "never set" sentinel separating them from a member who
 * chose it.
 *
 * <p>Two properties are load-bearing and neither is an implementation detail:
 *
 * <ul>
 *   <li><b>It resolves to a CONCRETE `HomeRange`, never to a sentinel.</b> The Range pill goes on
 *       showing exactly which window the member is looking at, and the URL, the cursor walk and the
 *       persisted `allTopRange` keep today's contract byte for byte. This is what keeps the slice
 *       free of a migration — `HomeFeedTopRange` is a CHECK-constrained column on
 *       `application_users`, so an `AUTO` value would have meant widening it.</li>
 *   <li><b>It runs BEFORE the state commit, never after.</b> The sort change and its resolved range
 *       land in one `setPageState` push, so rule 33 needs no replace exemption for this behaviour and
 *       the member sees the pill and the feed change together rather than a narrow-then-widen
 *       flicker.</li>
 * </ul>
 */

/**
 * Narrowest first. Resolution walks this in order and stops at the first window that clears the
 * threshold, so `all` is the terminal fallback and is never itself probed — reaching it means every
 * narrower window was thin, and `all` is by definition a superset of them.
 */
export const HOME_RANGE_NARROW_TO_WIDE: readonly HomeRange[] = ["day", "week", "month", "year", "all"];

/**
 * What counts as "still has content": one full page as `HomeFeedList` renders it
 * (`HOME_DISCUSSION_PAGE_SIZE`). Below this the member lands on a visibly stubby feed and reaches
 * for the Range pill anyway, which is the friction this removes. Deliberately not 1 — a two-post
 * "Today" is worse than a full "This week".
 */
export const AUTO_NARROW_MIN_ITEMS = 10;

export type HomeRangeProbe = (
    orderingStyle: HomeOrderingStyle,
    range: HomeRange,
) => Promise<DiscussionWithoutContentDTO[]>;

/**
 * The default probe: the same three endpoints the feed itself calls, first page, no cursor. A
 * dedicated server-side count endpoint would be one request instead of one-to-five and is the better
 * long-run shape, but it costs a controller, a `WebSecurityConfig` allowlist entry, a hand-written
 * client wrapper and a real-Postgres integration test — none of which this behaviour needs to be
 * correct.
 */
export const fetchHomeRangePage: HomeRangeProbe = async (orderingStyle, range) => {
    if (orderingStyle === "controversial") {
        const response = await ClientFactory.getMostControversialDiscussionsPage(range);
        return response?.data ?? [];
    }

    if (orderingStyle === "arguments") {
        const response = await ClientFactory.getMostArguedDiscussionsPage(range);
        return response?.data ?? [];
    }

    const response = await ClientFactory.getMostAgreedDiscussionsPage(range);
    return response?.data ?? [];
};

/**
 * The smallest window in {@link HOME_RANGE_NARROW_TO_WIDE} whose first page holds at least
 * {@link AUTO_NARROW_MIN_ITEMS} posts.
 *
 * <p>Probes are sequential and stop at the first success, so the common case is ONE extra request;
 * the worst case is four, only on a reading-set/sort combination where even `year` is thin — and
 * there the member was going to widen by hand anyway.
 *
 * <p><b>A probe that throws resolves to `fallbackRange`</b>, i.e. exactly today's behaviour. A failed
 * probe must never be the reason a member lands on an empty feed, so a network error degrades to the
 * remembered window rather than to the narrowest one.
 */
export async function resolveSmallestRangeWithContent(
    orderingStyle: HomeOrderingStyle,
    fallbackRange: HomeRange,
    probe: HomeRangeProbe = fetchHomeRangePage,
): Promise<HomeRange> {
    if (!homeOrderingSupportsRange(orderingStyle)) {
        return fallbackRange;
    }

    for (const range of HOME_RANGE_NARROW_TO_WIDE) {
        // `all` is the terminal fallback, not a candidate to probe: every narrower window already
        // came back thin, and widening further cannot make it thinner.
        if (range === "all") {
            return "all";
        }

        try {
            const items = await probe(orderingStyle, range);
            if (items.length >= AUTO_NARROW_MIN_ITEMS) {
                return range;
            }
        } catch {
            return fallbackRange;
        }
    }

    return fallbackRange;
}

/**
 * The next rung WIDER than `current`, or `current` itself when it is already `all`.
 *
 * <p>Tier-2 finding 29. The two widen affordances — the filtered empty state and the end-of-feed
 * action — both jumped straight to `all`, skipping every rung between: a member who narrowed to
 * "Past 24h" and found it thin was handed "all time" or nothing, with no way to ask for the week.
 *
 * <p>Walks the SAME ladder `resolveSmallestRangeWithContent` narrows along, in the opposite
 * direction. One ordering, one place — a second literal list here is how the two halves of a control
 * drift apart.
 */
export function nextWiderRange(current: HomeRange): HomeRange {
    const index = HOME_RANGE_NARROW_TO_WIDE.indexOf(current);
    // An unrecognised range (an old persisted value, a hand-edited URL) resolves to `all` rather than
    // to `day`: widening must never make the feed narrower than it already was.
    if (index < 0) {
        return "all";
    }
    return HOME_RANGE_NARROW_TO_WIDE[Math.min(index + 1, HOME_RANGE_NARROW_TO_WIDE.length - 1)];
}
